import { TypeCell } from "@warda/library-ui/layout/StickyGrid";
import { KEY_ASSIGNMENTS, KEY_ATTRIBUTE_SETS, KEY_ENTITY_ID, KEY_ENTITY_STRUCTURE_ID, KEY_MEDIA, KEY_PUBLC_ATTRS, KEY_PUBLC_MEDIA, KEY_READY_ATTRS, KEY_READY_MEDIA, KEY_ROOT_DOCUMENT_ID, KEY_VIEW_CHECK, KEY_VIEW_DATA, KEY_VIEW_STATUS, } from "../constants";
import { INDEX_NAME, } from "../../../interfaces";
import { mergeProductDatas, getDataDictionaries } from "./";
import { getAttributeId } from "../getAttributeKey";
import { MAX_PAGINATION } from "../../../constants/keys";
import { msearchEs, searchEs } from "../../../api/fetchesApiSearch";
const getMSearchBody = (index, ids, _source) => ({
    index,
    body: {
        track_total_hits: true,
        from: 0,
        size: MAX_PAGINATION,
        sort: [],
        query: { terms: { _id: ids } },
        _source,
    },
});
const msearchIndex = async ({ aggregateType, itemsId, rootsId, loadMedia, loadRoots, loadReadyAttrs, loadReadyMedia, loadPublcAttrs, loadPublcMedia, }) => {
    const res = {
        rootsTabular: [],
        itemsTabularRoot: [],
        itemsMedia: [],
        itemsReady: [],
        itemsPublic: [],
    };
    if (!loadRoots)
        return res;
    const jsonBody = [
        getMSearchBody(INDEX_NAME.TABULAR, rootsId, [KEY_ATTRIBUTE_SETS]),
        getMSearchBody(INDEX_NAME.TABULARROOT, rootsId, undefined),
    ];
    const keysReady = [
        loadReadyMedia ? KEY_READY_MEDIA : null,
        loadReadyAttrs ? KEY_READY_ATTRS : null,
    ].filter(Boolean);
    const keysPublc = [
        loadPublcMedia ? KEY_PUBLC_MEDIA : null,
        loadPublcAttrs ? KEY_PUBLC_ATTRS : null,
    ].filter(Boolean);
    if (keysReady.length > 0) {
        jsonBody.push(getMSearchBody(INDEX_NAME.READY, itemsId, keysReady));
    }
    if (keysPublc.length > 0) {
        jsonBody.push(getMSearchBody(INDEX_NAME.PUBLC, itemsId, keysPublc));
    }
    if (loadMedia) {
        jsonBody.push(getMSearchBody(INDEX_NAME.MEDIA, itemsId, [KEY_MEDIA]));
    }
    const mres = await msearchEs({
        aggregateType,
        data: jsonBody,
    });
    res.rootsTabular = mres[0].items;
    res.itemsTabularRoot = mres[1].items;
    const indexReady = jsonBody.findIndex((i) => i.index === INDEX_NAME.READY);
    if (indexReady > 0) {
        res.itemsReady = mres[indexReady].items;
    }
    const indexPublc = jsonBody.findIndex((i) => i.index === INDEX_NAME.PUBLC);
    if (indexPublc > 0) {
        res.itemsPublic = mres[indexPublc].items;
    }
    const indexMedia = jsonBody.findIndex((i) => i.index === INDEX_NAME.MEDIA);
    if (indexMedia > 0) {
        res.itemsMedia = mres[indexMedia].items;
    }
    return res;
};
const findData = (a, keyValue) => {
    var _a;
    const res = (_a = a.find((i) => i.id === keyValue)) === null || _a === void 0 ? void 0 : _a.data;
    return res || {};
};
const searchHasAggs = (d) => {
    return !!d && !!d.aggs;
};
const searchHasSource = (d) => {
    return !!d && !!d._source && !!d._source.length;
};
const searchOnlyOutTabular = (d) => {
    const set = new Set([
        KEY_READY_ATTRS,
        KEY_READY_MEDIA,
        KEY_PUBLC_ATTRS,
        KEY_PUBLC_MEDIA,
    ]);
    return d._source.every((s) => set.has(s));
};
export function buildQueryWithEntityId(originalQuery) {
    var _a, _b, _c;
    if (((_a = originalQuery.query) === null || _a === void 0 ? void 0 : _a.ids) || ((_b = originalQuery.query) === null || _b === void 0 ? void 0 : _b.terms))
        return originalQuery;
    const queryCopy = { ...originalQuery };
    const bool = ((_c = queryCopy.query) === null || _c === void 0 ? void 0 : _c.bool)
        ? { ...queryCopy.query.bool }
        : { must: [] };
    const must = bool.must ? [...bool.must] : [];
    const hasEntityId = must.some((cond) => "exists" in cond && cond.exists.field === "entityId");
    if (!hasEntityId) {
        must.push({ exists: { field: "entityId" } });
    }
    queryCopy.query = { ...(queryCopy.query || {}), bool: { ...bool, must } };
    return queryCopy;
}
export const decorateSearchData = (d) => {
    // caso di nessun source -> richiedo il documento di tabular completo
    if (!searchHasSource(d))
        return { ...d, _source: [] };
    // caso di specifici source fuori da tabular -> richiedo il documento di tabular completo
    if (searchOnlyOutTabular(d))
        return { ...d, _source: [] };
    // caso "grid" -> richiedo solo gli attributi visibili come colonne
    // in questo caso aggiungo alcuni attributi necessari per il render corretto
    const sourceNew = new Set(d._source);
    sourceNew.add(KEY_ENTITY_ID);
    sourceNew.add(KEY_ROOT_DOCUMENT_ID);
    sourceNew.add(KEY_ATTRIBUTE_SETS);
    sourceNew.add(KEY_ENTITY_STRUCTURE_ID);
    // in questo caso aggiungo alcuni attributi necessari per il render della colonna MEDIA
    if (sourceNew.has(KEY_MEDIA)) {
        sourceNew.add(KEY_VIEW_DATA);
        sourceNew.add(KEY_VIEW_CHECK);
        sourceNew.add(KEY_VIEW_STATUS);
        sourceNew.add(KEY_ASSIGNMENTS);
    }
    return {
        ...d,
        _source: Array.from(sourceNew),
        query: buildQueryWithEntityId(d).query,
    };
};
const isLoadDicts = (d, columns) => {
    if (searchHasAggs(d))
        return false;
    if (searchHasSource(d)) {
        return !!d._source.some((key) => {
            const { attrId } = getAttributeId(key);
            const column = columns.find((c) => c.id === attrId);
            if (!column)
                return undefined;
            const type = column.type;
            const dict = new Set([
                TypeCell.DictionaryEntry,
                TypeCell.DictionaryEntries,
            ]);
            return dict.has(type);
        });
    }
    return true;
};
const isLoadByKey = (d, key) => {
    if (searchHasAggs(d))
        return false;
    if (searchHasSource(d)) {
        return d._source.some((k) => k === key);
    }
    return true;
};
const isLoadRoots = (d) => {
    if (searchHasAggs(d))
        return false;
    if (searchHasSource(d)) {
        return d._source.join() !== [KEY_ENTITY_ID].join();
    }
    return true;
};
export const howLoad = (d, columns) => {
    const loadMedia = isLoadByKey(d, KEY_MEDIA);
    return {
        loadDicts: isLoadDicts(d, columns),
        loadMedia,
        loadRoots: isLoadRoots(d),
        loadReadyAttrs: isLoadByKey(d, KEY_READY_ATTRS),
        loadReadyMedia: isLoadByKey(d, KEY_READY_MEDIA) || loadMedia,
        loadPublcAttrs: isLoadByKey(d, KEY_PUBLC_ATTRS),
        loadPublcMedia: isLoadByKey(d, KEY_PUBLC_MEDIA) || loadMedia,
    };
};
const searchEsItems = async ({ aggregateType, data, columns, categories, }) => {
    // NOTE qui non va nessun try catch
    // l'errore va gestito caso per caso
    const res = await searchEs({
        aggregateType,
        index: INDEX_NAME.TABULAR,
        data: decorateSearchData(data),
    });
    const { items: itemsTabular, itemsTotal, aggregations } = res;
    if (!itemsTabular.length) {
        return {
            items: [],
            itemsTotal,
            aggregations,
        };
    }
    const { loadDicts, loadMedia, loadRoots, loadReadyAttrs, loadReadyMedia, loadPublcAttrs, loadPublcMedia, } = howLoad(data, columns);
    const itemsId = itemsTabular.map((x) => x.data[KEY_ENTITY_ID]);
    const rootsId = itemsTabular.map((x) => x.data[KEY_ROOT_DOCUMENT_ID]);
    const [{ rootsTabular, itemsTabularRoot, itemsReady, itemsMedia, itemsPublic }, dictionaries,] = await Promise.all([
        msearchIndex({
            aggregateType,
            itemsId,
            rootsId,
            loadMedia,
            loadRoots,
            loadReadyAttrs,
            loadReadyMedia,
            loadPublcAttrs,
            loadPublcMedia,
        }),
        getDataDictionaries({
            aggregateType,
            items: itemsTabular,
            columns,
            error: !loadDicts,
        }),
    ]);
    const items = itemsTabular.map(({ id, data }) => {
        const rootId = data[KEY_ROOT_DOCUMENT_ID];
        const rootItem = findData(rootsTabular, rootId);
        const tabularroot = findData(itemsTabularRoot, rootId);
        const ready = findData(itemsReady, id);
        const media = findData(itemsMedia, id);
        const publications = findData(itemsPublic, id);
        return mergeProductDatas({
            tabular: data,
            tabularroot,
            ready,
            media,
            publications,
            dictionaries,
            attributeSets: rootItem[KEY_ATTRIBUTE_SETS] || [],
            columns,
            categories,
            businessIds: [],
        });
    });
    return {
        aggregations,
        itemsTotal,
        items,
    };
};
export default searchEsItems;
